import { apiErrorToToast } from "@/lib/utils";
import {
  updateQuizAnswer,
  UpdateQuizAnswerRequestParams,
} from "@/requests/questionnaries/update-quiz-answer.request";
import { ApiErrorResponse } from "@/types/apis";
import { TopicWithRefreshStatus } from "@/types/questionnaries";
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

export type UpdateQuizAnswerMutationParams = UpdateQuizAnswerRequestParams & {
  dimension: string;
  topic: string;
};

/**
 * Simulate backend operations to update the answer
 */
const onMutate = async (
  variables: UpdateQuizAnswerMutationParams,
  queryClient: QueryClient,
) => {
  const queryKey = [
    "companies",
    variables.companyId,
    "quiz",
    variables.quizId,
    "responses",
    {
      dimension: variables.dimension,
      topic: variables.topic,
    },
  ];

  // Cancel ongoing queries for the topic
  await queryClient.cancelQueries({
    queryKey,
  });
};

export const useUpdateQuizAnswer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: UpdateQuizAnswerMutationParams) =>
      updateQuizAnswer(variables),
    onMutate: (variables) => onMutate(variables, queryClient),
    onError: (error: AxiosError<ApiErrorResponse>) => {
      apiErrorToToast(error);
    },
    onSuccess: async (_, variables) => {
      const queryKey = [
        "companies",
        variables.companyId,
        "quiz",
        variables.quizId,
        "dimension",
        variables.dimension,
        "topic",
        variables.topic,
      ];

      queryClient.invalidateQueries({ queryKey, refetchType: "none" });

      queryClient.setQueryData(queryKey, (oldData: TopicWithRefreshStatus) => ({
        ...oldData,
        needsRefresh: true,
      }));

      // Backend has a delay of 5s to update the dimension summary (and
      // therefore the topic summary) after a response is saved.
      // Use 10 segs to avoid a race condition
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey,
        });

        queryClient.invalidateQueries({
          queryKey: [
            "companies",
            variables.companyId,
            "quiz",
            variables.quizId,
            "details",
          ],
        });
      }, 15000);
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "companies",
          variables.companyId,
          "quiz",
          variables.quizId,
          "responses",
          {
            dimension: variables.dimension,
            topic: variables.topic,
          },
        ],
      });
    },
  });
};
